import { cloneDeep } from "lodash";
import {
  CorruptedGcpType,
  GcpRequestInstruction,
  IExistingGcp,
  IGCPRequestV2,
  PlacementStatus,
} from "../../../apiFacades/bapi/gcpRequests";

const GCPREQUEST_PERSISTED = "GCPREQUEST_PERSISTED";
export const persistGcpRequest = (gcpRequest: IGCPRequestState) => ({
  type: GCPREQUEST_PERSISTED,
  payload: gcpRequest,
});

const GCPREQUEST_CORRUPTED_NAIL_STATUS_UPDATED =
  "GCPREQUEST_CORRUPTED_NAIL_STATUS_UPDATED";
export const updateCorruptedNailStatus = (
  nailId: string,
  status: string,
): IAction<IAssignCorruptedStatusPayload> => ({
  type: GCPREQUEST_CORRUPTED_NAIL_STATUS_UPDATED,
  payload: { nailId, status },
});

const GCPREQUEST_CORRUPTED_NAIL_STATUS_WAS_RESET =
  "GCPREQUEST_CORRUPTED_NAIL_STATUS_WAS_RESET";
export const resetCorruptedNailStatus = (nailId: string): IAction<string> => ({
  type: GCPREQUEST_CORRUPTED_NAIL_STATUS_WAS_RESET,
  payload: nailId,
});

const GCPREQUEST_SUGGESTED_FLIGHT_MARKER_ADDED =
  "GCPREQUEST_SUGGESTED_FLIGHT_MARKER_ADDED";
export const addSuggestedFlightMarker = (
  newNail: ISuggestedFlightMarker,
): IAction<ISuggestedFlightMarker> => ({
  type: GCPREQUEST_SUGGESTED_FLIGHT_MARKER_ADDED,
  payload: newNail,
});

const GCPREQUEST_SUGGESTED_FLIGHT_MARKER_REMOVED =
  "GCPREQUEST_SUGGESTED_FLIGHT_MARKER_REMOVED";
export const removeSuggestedFlightMarker = (
  index: number,
): IAction<number> => ({
  type: GCPREQUEST_SUGGESTED_FLIGHT_MARKER_REMOVED,
  payload: index,
});

const GCPREQUEST_INSTALLED_FLIGHT_MARKER_ADDED =
  "GCPREQUEST_INSTALLED_FLIGHT_MARKER_ADDED";
export const addInstalledFlightMarkerToState = (
  installedFlightMarker: IInstalledFlightMarker,
): IAction<IInstalledFlightMarker> => ({
  type: GCPREQUEST_INSTALLED_FLIGHT_MARKER_ADDED,
  payload: installedFlightMarker,
});
export const createInstalledFlightMarker = (
  id: string,
  coordinates: ILatLng,
) => ({
  id,
  tsCreatedMs: Date.now(),
  coordinates,
});

const GCPREQUEST_INSTALLED_FLIGHT_MARKER_REMOVED =
  "GCPREQUEST_INSTALLED_FLIGHT_MARKER_REMOVED";
export const removeInstalledFlightMarkerFromState = (
  id: string,
): IAction<string> => ({
  type: GCPREQUEST_INSTALLED_FLIGHT_MARKER_REMOVED,
  payload: id,
});

export type ICorruptionStatus = string;

export const isSupportedCorruptionStatus = (status: string): boolean =>
  status &&
  [
    CorruptedGcpType.REPLACE_BROKEN,
    CorruptedGcpType.REPLACE_SMALL,
    CorruptedGcpType.CLEANUP_STUFF,
    CorruptedGcpType.CLEANUP_DIRT,
    CorruptedGcpType.CLEANUP_REPAINT,
    CorruptedGcpType.FIX_COORDINATES,
    CorruptedGcpType.CHECK_CONDITION,
    CorruptedGcpType.OTHER,
  ].includes(status as CorruptedGcpType);

interface IAssignCorruptedStatusPayload {
  nailId: string;
  status: string;
}

export interface ILatLng {
  lat: number;
  lng: number;
}

export interface ISuggestedFlightMarker {
  id: string;
  tsCreatedMs: number;
  coordinates: ILatLng;
}

export interface IInstalledFlightMarker {
  id: string;
  tsCreatedMs: number;
  coordinates: ILatLng;
}

export interface ICorruptedFlightMarkersStatusList {
  [key: string]: ICorruptionStatus;
}

export interface IGCPRequestState {
  corruptedFlightMarkers: ICorruptedFlightMarkersStatusList;
  // js objects are not ordered, so an extra field is needed
  corruptedFlightMarkersOrder: string[];
  suggestedFlightMarkers: ISuggestedFlightMarker[];
  installedFlightMarkers: IInstalledFlightMarker[];
  // my assumption is that gcp is any point that can be used for georeferencing
  // while flight marker is explicitly a cross
  existingGcps: IExistingGcp[];

  // quick hack to avoid rewriting all csv related functionality to use zustand state
  instructions: Record<string, GcpRequestInstruction[]>;

  loading?: boolean;
}

export const defaultState: IGCPRequestState = {
  corruptedFlightMarkers: {},
  corruptedFlightMarkersOrder: [],
  suggestedFlightMarkers: [],
  installedFlightMarkers: [],
  existingGcps: [],
  instructions: {},
  loading: true,
};
export default function gcpRequestReducer(
  state: IGCPRequestState = defaultState,
  action: IAction<any>,
): IGCPRequestState {
  switch (action.type) {
    case GCPREQUEST_PERSISTED:
      return action.payload;
    case GCPREQUEST_CORRUPTED_NAIL_STATUS_UPDATED:
      return {
        ...state,
        corruptedFlightMarkers: {
          ...state.corruptedFlightMarkers,
          [action.payload.nailId]: action.payload.status,
        },
      };
    case GCPREQUEST_CORRUPTED_NAIL_STATUS_WAS_RESET:
      return mResetCorruptedNailStatus(state, action.payload);
    case GCPREQUEST_SUGGESTED_FLIGHT_MARKER_ADDED:
      return mAddSuggestedFlightMarker(state, action.payload);
    case GCPREQUEST_SUGGESTED_FLIGHT_MARKER_REMOVED:
      return mRemoveSuggestedFlightMarker(state, action.payload);
    case GCPREQUEST_INSTALLED_FLIGHT_MARKER_ADDED:
      return mAddInstalledFlightMarker(state, action.payload);
    case GCPREQUEST_INSTALLED_FLIGHT_MARKER_REMOVED:
      return mRemoveInstalledFlightMarker(state, action.payload);
    default:
      return state;
  }
}

// Converting to the old data format so that the existing stores and logic would work
// without huge refactoring
export function convertFromGCPRequestV2ToGcpRequest(
  gcpRequest: IGCPRequestV2,
  isPilot: boolean,
): IGCPRequestState {
  const corruptedFlightMarkers: ICorruptedFlightMarkersStatusList = {};
  const suggestedFlightMarkers: ISuggestedFlightMarker[] = [];
  const installedFlightMarkers: IInstalledFlightMarker[] = [];

  for (const marker of gcpRequest.corrupted) {
    corruptedFlightMarkers[marker.flightMarkerId] = marker.type;
  }

  for (const marker of gcpRequest.suggested) {
    const markerData = {
      tsCreatedMs: marker.tsCreated,
      id: marker.id,
      coordinates: {
        lat: marker.coordinates.y,
        lng: marker.coordinates.x,
      },
    };

    suggestedFlightMarkers.push(markerData);

    if (marker.placementStatus === PlacementStatus.COMPLETED) {
      installedFlightMarkers.push(markerData);
    }
  }

  return {
    corruptedFlightMarkers,
    suggestedFlightMarkers,
    installedFlightMarkers,
    // GIS or OPS might ask to revert this, this is why it's here and not in BAPI
    // We are hiding Existing Controls from clients, but they very rarely open this App
    existingGcps: isPilot ? gcpRequest.existing : [],
    instructions: gcpRequest.instructions,
    corruptedFlightMarkersOrder: gcpRequest.corrupted.map(
      (c) => c.flightMarkerId,
    ),
  };
}

// these functions have prefix "m" (modifier) to avoid collisions with corresponding action creators

const mResetCorruptedNailStatus = (
  data: IGCPRequestState,
  nailId: string,
): IGCPRequestState => {
  const newData = cloneDeep(data);
  delete newData.corruptedFlightMarkers[nailId];
  newData.corruptedFlightMarkersOrder =
    newData.corruptedFlightMarkersOrder.filter((id) => id !== nailId);
  return newData;
};

const mAddSuggestedFlightMarker = (
  data: IGCPRequestState,
  newFlightMarker: ISuggestedFlightMarker,
): IGCPRequestState => ({
  ...data,
  suggestedFlightMarkers: [...data.suggestedFlightMarkers, newFlightMarker],
});

const mRemoveSuggestedFlightMarker = (
  data: IGCPRequestState,
  index: number,
) => {
  const suggestedFlightMarkers = cloneDeep(data.suggestedFlightMarkers);
  if (index < 0 || index >= suggestedFlightMarkers.length) {
    throw new Error("invalid index");
  }
  suggestedFlightMarkers.splice(index, 1);
  return {
    ...data,
    suggestedFlightMarkers,
  };
};

const mAddInstalledFlightMarker = (
  data: IGCPRequestState,
  newFlightMarker: IInstalledFlightMarker,
): IGCPRequestState => ({
  ...data,
  installedFlightMarkers: [...data.installedFlightMarkers, newFlightMarker],
});

const mRemoveInstalledFlightMarker = (
  gcpRequestState: IGCPRequestState,
  flightMarkerId: string,
) => {
  const { installedFlightMarkers } = gcpRequestState;
  const index = installedFlightMarkers.findIndex(
    ({ id }) => id === flightMarkerId,
  );
  installedFlightMarkers.splice(index, 1);
  return {
    ...gcpRequestState,
    installedFlightMarkers,
  };
};

// getters

export function getNailsWithStatus(
  state: IGCPRequestState,
  status: string,
): string[] {
  const cn = state.corruptedFlightMarkers;
  return Object.keys(cn).filter((nailId: string) => cn[nailId] === status);
}

export const getFlightMarkerCorruptionStatus = (
  gcpRequestState: IGCPRequestState,
  flightMarkerId: string,
): ICorruptionStatus =>
  gcpRequestState.corruptedFlightMarkers[flightMarkerId] || null;

export const getSuggestedFlightMarkerById = (
  { suggestedFlightMarkers }: IGCPRequestState,
  flightMarkerId: string,
) => suggestedFlightMarkers.find(({ id }) => id === flightMarkerId);

export const getInstalledFlightMarkerById = (
  { installedFlightMarkers }: IGCPRequestState,
  flightMarkerId: string,
) => installedFlightMarkers.find(({ id }) => id === flightMarkerId);
